import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { Link } from "react-router-dom";

interface DestinationTraffic {
  path: string;
  destinationId: string | null;
  name: string;
  pageViews: number;
  sessions: number;
  avgSessionDuration: number;
  bounceRate: number;
}

interface TripTraffic {
  path: string;
  tripId: string | null;
  title: string;
  pageViews: number;
  sessions: number;
  bounceRate: number;
}

interface TrafficOverview {
  totalPageViews: number;
  totalSessions: number;
  avgBounceRate: number;
  avgSessionDuration: number;
}

interface MonthlyTraffic {
  month: string;
  pageViews: number;
  sessions: number;
}

interface TrafficChartProps {
  destinations: DestinationTraffic[];
  trips: TripTraffic[];
  overview: TrafficOverview;
  byMonth: MonthlyTraffic[];
  isLoading?: boolean;
  error?: string | null;
}

import { formatMonth, formatCompactNumber } from ".";

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

export default function TrafficChart({
  destinations,
  trips,
  overview,
  byMonth,
  isLoading,
  error,
}: TrafficChartProps) {
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="card animate-pulse">
          <div className="mb-4 h-6 w-48 rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-64 rounded bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="py-8 text-center">
          <p className="text-red-500 dark:text-red-400">{error}</p>
          <p className="mt-2 text-sm text-gray-500">
            Check Google Analytics settings in environment variables.
          </p>
        </div>
      </div>
    );
  }

  const topDestinations = destinations.slice(0, 10);
  const topTrips = trips.slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Overview Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Page views</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {overview.totalPageViews.toLocaleString("en-US")}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Sessions</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {overview.totalSessions.toLocaleString("en-US")}
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Avg. bounce rate</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {overview.avgBounceRate.toFixed(1)}%
          </p>
        </div>
        <div className="card">
          <p className="text-sm text-gray-500 dark:text-gray-400">Avg. session duration</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {formatDuration(overview.avgSessionDuration)}
          </p>
        </div>
      </div>

      {/* Traffic Trend Chart */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          Traffic over time
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={byMonth} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorPageViews" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="month"
                tickFormatter={formatMonth}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={{ stroke: "#e5e7eb" }}
              />
              <YAxis
                tickFormatter={formatCompactNumber}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={{ stroke: "#e5e7eb" }}
                width={60}
              />
              <Tooltip
                formatter={(value: number, name: string) => [
                  value.toLocaleString("en-US"),
                  name === "pageViews" ? "Page views" : "Sessions",
                ]}
                labelFormatter={formatMonth}
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                }}
              />
              <Area
                type="monotone"
                dataKey="pageViews"
                name="pageViews"
                stroke="#3b82f6"
                strokeWidth={2}
                fill="url(#colorPageViews)"
              />
              <Area
                type="monotone"
                dataKey="sessions"
                name="sessions"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#colorSessions)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Destinations and Trips */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Top Destinations */}
        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            Top destinations
          </h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topDestinations}
                layout="vertical"
                margin={{ top: 5, right: 20, left: 80, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  type="number"
                  tickFormatter={formatCompactNumber}
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 11, fill: "#6b7280" }}
                  width={75}
                />
                <Tooltip
                  formatter={(value: number) => [value.toLocaleString("en-US"), "Views"]}
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                />
                <Bar dataKey="pageViews" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Trips */}
        <div className="card">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">Top trips</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="py-2 text-left font-medium text-gray-500 dark:text-gray-400">
                    Trip
                  </th>
                  <th className="py-2 text-right font-medium text-gray-500 dark:text-gray-400">
                    Views
                  </th>
                  <th className="py-2 text-right font-medium text-gray-500 dark:text-gray-400">
                    Bounce
                  </th>
                </tr>
              </thead>
              <tbody>
                {topTrips.map((trip, _index) => (
                  <tr key={trip.path} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2">
                      {trip.tripId ? (
                        <Link
                          to={`/trips/${trip.tripId}`}
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                        >
                          {trip.title}
                        </Link>
                      ) : (
                        <span className="text-gray-900 dark:text-white">{trip.title}</span>
                      )}
                    </td>
                    <td className="py-2 text-right text-gray-900 dark:text-white">
                      {trip.pageViews.toLocaleString("en-US")}
                    </td>
                    <td className="py-2 text-right">
                      <span
                        className={`${
                          trip.bounceRate > 60
                            ? "text-red-500"
                            : trip.bounceRate > 40
                              ? "text-yellow-500"
                              : "text-green-500"
                        }`}
                      >
                        {trip.bounceRate.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Destinations Table */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          Detailed destination statistics
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="py-3 text-left font-medium text-gray-500 dark:text-gray-400">
                  Destination
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Views
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Sessions
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Avg. duration
                </th>
                <th className="py-3 text-right font-medium text-gray-500 dark:text-gray-400">
                  Bounce rate
                </th>
              </tr>
            </thead>
            <tbody>
              {destinations.map((dest) => (
                <tr
                  key={dest.path}
                  className="border-b border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800/50"
                >
                  <td className="py-3">
                    {dest.destinationId ? (
                      <Link
                        to={`/destinations/${dest.destinationId}`}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                      >
                        {dest.name}
                      </Link>
                    ) : (
                      <span className="text-gray-900 dark:text-white">{dest.name}</span>
                    )}
                  </td>
                  <td className="py-3 text-right font-medium text-gray-900 dark:text-white">
                    {dest.pageViews.toLocaleString("en-US")}
                  </td>
                  <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                    {dest.sessions.toLocaleString("en-US")}
                  </td>
                  <td className="py-3 text-right text-gray-600 dark:text-gray-400">
                    {formatDuration(dest.avgSessionDuration)}
                  </td>
                  <td className="py-3 text-right">
                    <span
                      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                        dest.bounceRate > 60
                          ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                          : dest.bounceRate > 40
                            ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                            : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                      }`}
                    >
                      {dest.bounceRate.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
